import { Datepicker } from '@fountain/fountain-ui-components';
import { Button, Typography } from '@material-ui/core';
import { format, parse, subDays } from 'date-fns';
import React, { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import globalMessages from 'shared/global/messages';
import { getDateFormatPattern } from 'utils/datePlaceholder';

import { messages } from '../messages';
import {
  ConversationFilterMenu,
  FilterMenuItem,
} from './ConversationFilterMenu';
import {
  ConversationFilters,
  CustomRangeError,
  getCustomRangeError,
} from './conversationFilters';
import { useStyles } from './styles';

const presets = {
  '7d': messages.dateLast7Days,
  '30d': messages.dateLast30Days,
  '90d': messages.dateLast90Days,
};
const errors: Record<CustomRangeError, typeof messages.dateRangeMissing> = {
  missing: messages.dateRangeMissing,
  order: messages.dateRangeOrder,
  future: messages.dateRangeFuture,
  tooWide: messages.dateRangeTooWide,
};

export const ConversationDateFilter = ({
  filters,
  onChange,
}: {
  filters: ConversationFilters;
  onChange: (filters: ConversationFilters) => void;
}) => {
  const intl = useIntl();
  const classes = useStyles();
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const [custom, setCustom] = useState(false);
  const [from, setFrom] = useState<Date>();
  const [to, setTo] = useState<Date>();
  const [error, setError] = useState<CustomRangeError | null>(null);
  const fields = useRef<HTMLDivElement>(null);
  const label =
    filters.from && filters.to
      ? intl.formatDateTimeRange(parse(filters.from), parse(filters.to), {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : intl.formatMessage(presets[filters.range]);

  const close = () => {
    setAnchor(null);
    setCustom(false);
  };
  const apply = () => {
    const blocker = getCustomRangeError(from, to);
    setError(blocker);
    if (blocker || !from || !to) {
      fields.current?.querySelector<HTMLElement>('[role="button"]')?.focus();
      return;
    }
    onChange({
      ...filters,
      range: '30d',
      from: format(from, 'YYYY-MM-DD'),
      to: format(to, 'YYYY-MM-DD'),
    });
    close();
  };

  return (
    <ConversationFilterMenu
      label={label}
      name={intl.formatMessage(globalMessages.date)}
      narrowed={!!filters.from || filters.range !== '30d'}
      anchor={anchor}
      onOpen={setAnchor}
      onClose={close}
      dialog={custom}
      autoFocus={!custom}
    >
      {custom ? (
        <div className={classes.customRange}>
          <div
            className={classes.dateFields}
            ref={fields}
            aria-describedby={error ? 'conversation-date-error' : undefined}
          >
            {[
              {
                id: 'conversation-from',
                message: messages.startDate,
                value: from,
                change: setFrom,
              },
              {
                id: 'conversation-to',
                message: messages.endDate,
                value: to,
                change: setTo,
              },
            ].map(field => (
              <div key={field.id} className={classes.dateField}>
                <Typography id={field.id} variant="body2">
                  {intl.formatMessage(field.message)}
                </Typography>
                <Datepicker
                  ariaLabelledBy={field.id}
                  locale={intl.locale}
                  value={field.value}
                  onDateChange={value => {
                    field.change(value as Date | undefined);
                    setError(null);
                  }}
                  renderDate={value =>
                    intl.formatDate(value, { dateStyle: 'short' })
                  }
                  renderPlaceholder={() => getDateFormatPattern(intl.locale)}
                />
              </div>
            ))}
          </div>
          {error && (
            <Typography
              id="conversation-date-error"
              variant="body2"
              className={classes.dateError}
              role="alert"
            >
              {intl.formatMessage(errors[error])}
            </Typography>
          )}
          <div className={classes.dateActions}>
            <Button onClick={close}>
              {intl.formatMessage(globalMessages.cancel)}
            </Button>
            <Button variant="contained" color="primary" onClick={apply}>
              {intl.formatMessage(globalMessages.apply)}
            </Button>
          </div>
        </div>
      ) : (
        [
          ...Object.entries(presets).map(([range, message]) => (
            <FilterMenuItem
              key={range}
              selected={!filters.from && filters.range === range}
              onClick={() => {
                onChange({
                  ...filters,
                  range: range as ConversationFilters['range'],
                  from: undefined,
                  to: undefined,
                });
                close();
              }}
            >
              {intl.formatMessage(message)}
            </FilterMenuItem>
          )),
          <FilterMenuItem
            key="custom"
            onClick={() => {
              setFrom(
                filters.from ? parse(filters.from) : subDays(new Date(), 29),
              );
              setTo(filters.to ? parse(filters.to) : new Date());
              setError(null);
              setCustom(true);
            }}
          >
            {intl.formatMessage(messages.dateCustomRange)}
          </FilterMenuItem>,
        ]
      )}
    </ConversationFilterMenu>
  );
};
